<template>
  <v-col>
    <v-row>
      <v-card-subtitle
        class="subtitle-1 font-weight-medium darkestblue--text"
      >
        <v-row>
          <v-col>
            <span :class="themeTextClass('darkblue', 'primary')">
              Merge
            </span>

            <font-awesome-icon
              :icon="['fas', 'circle-question']"
              size="sm"
              :class="`ml-1 ${showInfo ? 'primary' : 'midgray'}--text`"
              title="Instructions"
              @click="toggleInfo"
            />
          </v-col>
        </v-row>

        <v-row
          v-if="showInfo"
          class="mt-0"
        >
          <v-col>
            <p class="caption coolgray--text">
              Merge rows to summarize metrics across items.
            </p>

            <p class="caption coolgray--text">
              <b>Example</b> Select Advertiser, Order, and Line Item to merge items with
              all the same dimensions except those identified here as ignorable.
            </p>

            <p class="mb-0 caption coolgray--text">
              <b>Hint</b> Select all options to combine all data into one row of summarized metrics.
            </p>
          </v-col>
        </v-row>
      </v-card-subtitle>
    </v-row>

    <v-row class="px-4">
      <v-select
        v-model="mergedBy"
        label="Property"
        :items="currentMergingOptions"
        dense
        multiple
        clearable
      >
        <template v-slot:prepend-item>
          <v-list-item @click="toggleSelectAll('select')">
            <v-list-item-content ripple>
              <v-list-item-title class="cursor-pointer align-center row">
                <v-col class="col-1">
                  <Checkbox
                    :input-value="hasSelectedAll"
                    :color="hasSelectedAll ? 'primary' : 'grey'"
                    @click.native.stop
                    @change="toggleSelectAll('select')"
                  />
                </v-col>

                <v-col
                  :class="`pl-2 ${themeTextClass('black', 'white')} font-weight-medium text-wrap body-2`"
                >
                  <span>Select All</span>
                </v-col>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item @click="toggleSelectAll('toggle')">
            <v-list-item-content ripple>
              <v-list-item-title class="cursor-pointer align-center row">
                <v-col class="col-1">
                  <font-awesome-icon
                    :icon="['fas', 'list-check']"
                    class="grey--text"
                  />
                </v-col>

                <v-col
                  :class="`pl-2 ${themeTextClass('black', 'white')} font-weight-medium text-wrap body-2`"
                >
                  <span>Toggle All</span>
                </v-col>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-divider class="mt-2" />
        </template>

        <template v-slot:item="option">
          <checkbox-option-row
            :checked="mergedBy.includes(option.item.value)"
            :color="mergedBy.includes(option.item.value) ? 'primary' : 'midgray'"
            :title="String(option.item.text || option.item.value || '')"
            :title-class="`${themeTextClass('black', 'white')} font-weight-medium text-wrap body-2`"
            checkbox-class=""
            @toggle="toggleOption(option.item)"
          />
        </template>
      </v-select>
    </v-row>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import themeClasses from '@/mixins/themeClasses'
import Checkbox from '@/tools/components/Checkbox.vue'
import CheckboxOptionRow from '@/tools/components/filter/CheckboxOptionRow.vue'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters, mapState } = createNamespacedHelpers('tools')

export default {
  name: 'MergeTool',
  components: {
    Checkbox,
    CheckboxOptionRow,
  },
  mixins: [themeClasses],
  data() {
    return {
      showInfo: false,
    }
  },
  computed: {
    ...mapAuthGetters(['inDarkMode']),
    ...mapGetters([
      'currentMergeByProperties',
      'currentMerging',
      'currentMergingOptions',
      'reportKey',
    ]),
    ...mapState({
      isCurrentlyMerging: (state) => state.isCurrentlyMerging,
    }),
    mergedBy: {
      get() {
        return (this.isCurrentlyMerging && this.currentMerging) || this.currentMergeByProperties
      },
      set(mergeByProperties) {
        const { reportKey } = this

        this.$store.commit('tools/merging', { mergeByProperties, reportKey })
        this.$store.commit('tools/isCurrentlyMerging', true)

        return mergeByProperties
      },
    },
    hasSelectedAll() {
      const { currentMergingOptions, mergedBy } = this
      const totalSelected = mergedBy.length
      const totalOptions = currentMergingOptions.length
      const hasSelectedAll = totalSelected === totalOptions

      return totalOptions > 0 && hasSelectedAll
    },
    hasSelectedSome() {
      return this.mergedBy.length > 0 && !this.hasSelectedAll
    },
  },
  methods: {
    toggleInfo() {
      this.showInfo = !this.showInfo
    },
    toggleSelectAll(selectOrToggle) {
      const { hasSelectedAll } = this
      const isToggling = selectOrToggle === 'toggle'

      const selectAll = () => {
        this.mergedBy = this.currentMergingOptions.map((option) => option.value)
      }
      const unselectAll = () => {
        this.mergedBy = []
      }
      const toggleAll = () => {
        const filterSelected = (option) => !this.mergedBy.includes(option.value)
        this.mergedBy = this.currentMergingOptions.filter(filterSelected).map((option) => option.value)
      }

      if (isToggling) {
        toggleAll()
      } else if (hasSelectedAll) {
        unselectAll()
      } else {
        selectAll()
      }
    },
    toggleOption(item) {
      const index = this.mergedBy.indexOf(item.value)

      if (index > -1) {
        // Remove item
        const newMergedBy = [...this.mergedBy]
        newMergedBy.splice(index, 1)
        this.mergedBy = newMergedBy
      } else {
        // Add item
        this.mergedBy = [...this.mergedBy, item.value]
      }
    },
  },
}
</script>
